import { Suspense, lazy } from 'react';

import { Button } from '../ui/button';

const ChevronLeft = lazy(() =>
  import('lucide-react').then(mod => ({ default: mod.ChevronLeft })),
);
const ChevronRight = lazy(() =>
  import('lucide-react').then(mod => ({ default: mod.ChevronRight })),
);

interface SolveBottomBarProps {
  currentIndex: number;
  totalCount: number;
  remainingSubmissions: number;
  isSubmitting: boolean;
  isTempSubmitting: boolean;
  onPrev: () => void;
  onNext: () => void;
  onTempSubmit: () => void;
  onSubmit: () => void;
}

const SolveBottomBar = ({
  currentIndex,
  totalCount,
  remainingSubmissions,
  isSubmitting,
  isTempSubmitting,
  onPrev,
  onNext,
  onTempSubmit,
  onSubmit,
}: SolveBottomBarProps) => {
  // 제출/임시저장 중에는 두 버튼 모두 비활성(중복 요청 방지), 진행 중인 동작만 라벨을 바꿔 피드백. UX-05
  const isBusy = isSubmitting || isTempSubmitting;
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < totalCount - 1;

  return (
    <div
      className="sticky bottom-0 z-20 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-center justify-between gap-2 px-3 md:px-6 py-2.5">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canPrev}
            onClick={onPrev}
            className="h-9 px-2"
          >
            <Suspense fallback={<div className="h-4 w-4" />}>
              <ChevronLeft className="h-4 w-4" />
            </Suspense>
            <span className="hidden sm:inline ml-1">이전</span>
          </Button>
          <span className="text-xs md:text-sm text-gray-500 whitespace-nowrap px-1">
            {currentIndex + 1} / {totalCount}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canNext}
            onClick={onNext}
            className="h-9 px-2"
          >
            <span className="hidden sm:inline mr-1">다음</span>
            <Suspense fallback={<div className="h-4 w-4" />}>
              <ChevronRight className="h-4 w-4" />
            </Suspense>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isBusy}
            onClick={onTempSubmit}
            className="h-9"
          >
            {isTempSubmitting ? "저장 중..." : "임시저장"}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={remainingSubmissions <= 0 || isBusy}
            onClick={onSubmit}
            className="h-9"
          >
            {isSubmitting ? "제출 중..." : "답안 제출"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SolveBottomBar;
